import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import MobileEmptyState from './MobileEmptyState';
import MobileLoadingSkeleton from './MobileLoadingSkeleton';

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const MobileTickets = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('upcoming');

  useEffect(() => {
    if (user) fetchTickets();
  }, [user]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('event_registrations')
        .select(`
          *,
          events (
            id, event, title, start, end, venue, city, image_url, org, status, event_type
          )
        `)
        .eq('attendee_email', user.email)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = tickets.filter((t) => {
    const d = new Date(t.events?.end || t.events?.start);
    const now = new Date();
    if (filter === 'upcoming') return d >= now;
    if (filter === 'past') return d < now;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-4">
      {/* Header */}
      <header
        className="px-4 pb-4 bg-gradient-to-br from-primary-dark to-primary text-white rounded-b-3xl shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1.25rem)' }}
      >
        <h1 className="font-display text-2xl font-bold mb-1">My Tickets</h1>
        <p className="text-white/70 text-sm">View and manage your event tickets</p>
      </header>

      {/* Filter tabs */}
      <div className="px-4 mt-4">
        <div className="flex gap-2">
          {['upcoming', 'past', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`chip flex-1 py-2.5 rounded-xl text-sm font-semibold capitalize transition-colors ${
                filter === f ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 shadow-sm'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Ticket cards */}
      <div className="mt-4 px-4 space-y-3">
        {loading ? (
          <MobileLoadingSkeleton variant="card" count={2} />
        ) : filtered.length === 0 ? (
          <MobileEmptyState
            icon={<svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>}
            title={filter === 'upcoming' ? 'No upcoming tickets' : filter === 'past' ? 'No past tickets' : 'No tickets yet'}
            message="Register for an event to get your digital ticket."
            actionLabel="Browse Events"
            onAction={() => (window.location.href = '/events')}
          />
        ) : (
          filtered.map((ticket) => {
            const evt = ticket.events || {};
            const isPast = new Date(evt.end || evt.start) < new Date();
            return (
              <Link
                key={ticket.id}
                to={`/events/${evt.id}`}
                className="block bg-white dark:bg-gray-800 rounded-2xl shadow-card active:scale-[0.98] transition-transform overflow-hidden"
              >
                {/* Image strip */}
                {evt.image_url ? (
                  <div className="h-28 bg-gray-200 dark:bg-gray-700 overflow-hidden relative">
                    <img
                      src={evt.image_url}
                      alt={evt.event || evt.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    {ticket.payment_status === 'paid' && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                        PAID
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="h-20 bg-gradient-to-br from-primary/80 to-primary-dark flex items-center justify-center">
                    <span className="text-white/60 text-sm font-semibold">{evt.org || 'ACP'}</span>
                    {ticket.payment_status === 'paid' && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full">
                        PAID
                      </span>
                    )}
                  </div>
                )}

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      {evt.org && (
                        <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary mb-1.5">
                          {evt.org}
                        </span>
                      )}
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-white leading-snug line-clamp-2">
                        {evt.event || evt.title || 'Event'}
                      </h3>
                    </div>
                    {isPast && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 shrink-0">
                        PAST
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {formatDate(evt.start)}
                    </span>
                    {evt.city && (
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                        {evt.city}
                      </span>
                    )}
                  </div>

                  {/* Ticket code bar */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-400 font-mono">
                      {ticket.ticket_code || ticket.id?.slice(0, 8).toUpperCase()}
                    </span>
                    <span className="text-xs font-semibold text-primary flex items-center gap-1">
                      View Ticket
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </span>
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MobileTickets;
